import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useStytch, useStytchUser } from "@stytch/react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROOT } from "@/lib/links";
import { ArrowClockwise, Envelope } from "@phosphor-icons/react";

const otpSchema = z.object({
  code: z.string().min(6, {
    message: "Verification code must be 6 digits.",
  }),
});

type OtpFormValues = z.infer<typeof otpSchema>;

export default function EmailVerification() {
  const stytchClient = useStytch();
  const { user } = useStytchUser();
  const navigate = useNavigate();
  const [methodID, setMethodID] = useState<string | null>(null);

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      code: "",
    },
  });

  // Send verification email mutation
  const sendEmailMutation = useMutation({
    mutationKey: ["sendVerificationEmail"],
    mutationFn: async () => {
      if (!user?.emails[0]?.email) {
        throw new Error("No email address found");
      }

      const response = await stytchClient.otps.email.send(
        user.emails[0].email,
        {
          expiration_minutes: 10,
        }
      );

      if (response.status_code !== 200) {
        throw new Error("Failed to send verification email");
      }

      return response;
    },
    onSuccess: () => {
      toast({
        title: "Verification email sent",
        description: "Please check your email for the verification code.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to send email",
        description: error.message,
      });
    },
  });

  const verifyOtpMutation = useMutation({
    mutationKey: ["verifyOtp"],
    mutationFn: async (data: OtpFormValues) => {
      if (!user?.emails[0]?.email) {
        throw new Error("No email address found");
      }

      if (!methodID) {
        throw new Error("Method ID not available.");
      }

      const response = await stytchClient.otps.authenticate(
        data.code,
        methodID,
        {
          session_duration_minutes: 60 * 24 * 7, // 7 days
        }
      );

      if (response.status_code !== 200) {
        throw new Error("Invalid verification code");
      }

      return response;
    },
    onSuccess: () => {
      toast({
        title: "Email verified successfully",
        description: "You can now access all features.",
      });
      navigate(ROOT);
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: error.message,
      });
      form.reset();
    },
  });

  const onSubmit = async (data: OtpFormValues) => {
    verifyOtpMutation.mutate(data);
  };

  const handleSendEmail = () => {
    sendEmailMutation.mutateAsync().then((resp) => setMethodID(resp.method_id));
  };

  return (
    <div className="flex items-center justify-center w-full min-h-screen p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
              <Envelope className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Verify Your Email</CardTitle>
          <CardDescription>
            We need to verify your email address before you can continue.
            {user?.emails[0]?.email && (
              <span className="block mt-2 font-medium underline decoration-dashed">
                {user.emails[0].email}
              </span>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Button
            variant="default"
            onClick={handleSendEmail}
            disabled={sendEmailMutation.isPending}
            className="w-full"
          >
            {sendEmailMutation.isPending ? (
              <>
                <ArrowClockwise className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Envelope className="w-4 h-4 mr-2" />
                Send Verification Email
              </>
            )}
          </Button>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block text-center">
                      Enter the 6-digit code from your email
                    </FormLabel>
                    <FormControl>
                      <div className="flex justify-center">
                        <InputOTP
                          maxLength={6}
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={
                  verifyOtpMutation.isPending || form.watch("code").length !== 6
                }
              >
                {verifyOtpMutation.isPending ? "Verifying..." : "Verify Email"}
              </Button>
            </form>
          </Form>
        </CardFooter>
      </Card>
    </div>
  );
}
